import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BigNumber } from 'ethers'
import { Address, useTownsContext } from 'use-towns-client'

export const balanceQueryKey = (address: Address | undefined) => [
    'useBalance',
    address ?? 'waitingForAddress',
]

/**
 * Simple hook to replace wagmi's useBalance hook (wagmi)
 */
export function useBalance({
    address,
    enabled = true,
    staleTime,
    gcTime,
    fixedLength = 5,
    refetchInterval,
}: {
    address: Address | undefined
    enabled?: boolean
    watch?: boolean
    gcTime?: number
    staleTime?: number
    fixedLength?: number
    refetchInterval?: number
}) {
    const { baseProvider } = useTownsContext()
    const _queryKey = useMemo(() => balanceQueryKey(address), [address])
    const _refetchInterval = refetchInterval ?? 6_000

    return useQuery({
        queryKey: _queryKey,
        queryFn: async () => {
            if (!address) {
                return undefined
            }
            return baseProvider.getBalance(address)
        },
        select: (data: BigNumber | undefined) => {
            if (!data) {
                return
            }
            const value = data.toBigInt()

            return {
                // TODO: if we ever needed to, then chain.nativeCurrency.decimals
                decimals: 18,
                // TODO: if we ever needed to, then chain.nativeCurrency.symbol
                symbol: 'ETH',
                formatted: formatUnitsToFixedLength(value ?? '0', 18, fixedLength),
                value,
            }
        },
        enabled: enabled && !!address,
        refetchInterval: _refetchInterval,
        staleTime: staleTime ?? 60_000,
        gcTime: gcTime ?? 60_000 * 5,
    })
}

export function formatUnits(value: bigint, decimals: number = 18): string {
    if (value === 0n) {
        return '0'
    }

    const isNegative = value < 0n
    const absoluteValue = isNegative ? -value : value

    const stringValue = absoluteValue.toString().padStart(decimals + 1, '0')
    const integerPart = stringValue.slice(0, -decimals) || '0'
    const fractionalPart = stringValue.slice(-decimals).replace(/0+$/, '')

    const formattedValue = fractionalPart ? `${integerPart}.${fractionalPart}` : integerPart
    return isNegative ? `-${formattedValue}` : formattedValue
}

export function parseUnits(value: string, decimals: number = 18): bigint {
    if (!value || value === '.') {
        return 0n
    }

    const [integerPart = '0', fractionalPart = ''] = value.split('.')
    const paddedFractionalPart = fractionalPart.padEnd(decimals, '0').slice(0, decimals)
    const combinedValue = `${integerPart}${paddedFractionalPart}`

    return BigInt(combinedValue)
}

const compactNumberFormatter = Intl.NumberFormat('en-US', { notation: 'compact' })

export function formatUnitsToFixedLength(
    value: bigint,
    baseDecimals: number = 18,
    displayDecimals: number = 5,
    options?: { compact?: boolean },
): string {
    // Calculate the scaling factor to shift decimal places correctly
    const scaleFactor = 10n ** BigInt(Math.max(0, baseDecimals - displayDecimals))

    // Scale and round the BigInt value
    const roundedValue = (value + scaleFactor / 2n) / scaleFactor

    if (value === 0n) {
        return '0'
    }

    const smallestDisplayableValue = `0.${'0'.repeat(displayDecimals - 1)}1`
    if (roundedValue === 0n) {
        return `<${smallestDisplayableValue}`
    } else if (roundedValue === 1n && value < scaleFactor) {
        return `~${smallestDisplayableValue}`
    }

    const units = formatUnits(roundedValue, displayDecimals)

    if (options?.compact) {
        return compactNumberFormatter.format(Number(units))
    }

    return units
}
